import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  LinearProgress,
  Switch,
  apiClient,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@leap4/frontend';
import { MuiIcons } from '@leap4/frontend';
import { validationBaseUrl, rescrapeUrl, rescrapeStatusUrl } from '../../../../config/generals';
import {
  IValidationPhase,
  IValidationDayResult,
  IValidationStatusResponse,
  ALL_AD_SERVERS,
} from '../validation.interfaces';

const { Check, Close, Warning, Refresh, InfoOutlined, VerifiedUser, Email } = MuiIcons;

interface IRescrapeJob {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: {
    current: number;
    total: number;
    percentage: number;
    currentDate?: string;
  };
  summary?: {
    successful: number;
    failed: number;
  };
}

const fmtNum = (n: number) => n.toLocaleString('en-US');
const fmtMoney = (n: number) =>
  '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDiff = (n: number, isMoney: boolean) => {
  const sign = n > 0 ? '+' : '';
  return isMoney ? sign + fmtMoney(n) : sign + fmtNum(n);
};

const fmtShort = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getDefaultDates = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 13);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { start: fmt(start), end: fmt(end) };
};

type ValidationState = 'idle' | 'running' | 'done';

const MetricBlock: React.FC<{
  impressions: number;
  cost: number | null;
  revenue: number;
  profit: number | null;
  isDiff?: boolean;
}> = ({ impressions, cost, revenue, profit, isDiff }) => {
  const format = (v: number | null, isMoney: boolean) => {
    if (v === null) return '-';
    return isDiff ? fmtDiff(v, isMoney) : isMoney ? fmtMoney(v) : fmtNum(v);
  };

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.8, color: isDiff ? 'error.main' : 'text.primary', whiteSpace: 'pre' }}>
      <Box>imp: {format(impressions, false)}</Box>
      <Box>cost: {format(cost, true)}</Box>
      <Box>rev: {format(revenue, true)}</Box>
      <Box>profit: {format(profit, true)}</Box>
    </Box>
  );
};

export const ValidationPanel: React.FC = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [fullScan, setFullScan] = useState(false);
  const [selectedAdServers, setSelectedAdServers] = useState<string[]>([...ALL_AD_SERVERS]);
  const [state, setState] = useState<ValidationState>('idle');
  const [phases, setPhases] = useState<IValidationPhase[]>([]);
  const [dayResults, setDayResults] = useState<IValidationDayResult[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [fullScanWarningOpen, setFullScanWarningOpen] = useState(false);
  const [emailResults, setEmailResults] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [rescrapeJobs, setRescrapeJobs] = useState<Record<string, IRescrapeJob>>({});
  const rescrapePollingRefs = useRef<Record<string, NodeJS.Timeout>>({});

  const rescrapeRowKey = (datapointId: string, date: string) => `${datapointId}_${date}`;

  const getRescrapeStatus = (datapointId: string | undefined, date: string): IRescrapeJob | null => {
    if (!datapointId) return null;
    return rescrapeJobs[rescrapeRowKey(datapointId, date)] || null;
  };

  const isAnyRescraping = Object.values(rescrapeJobs).some(
    (job) => job.status === 'pending' || job.status === 'running'
  );

  const pollRescrapeJob = useCallback(async (jobId: string, rowKey: string) => {
    try {
      const response = await apiClient.get(`${rescrapeStatusUrl}/${jobId}`);
      const jobData: IRescrapeJob = response.data;
      setRescrapeJobs((prev) => ({ ...prev, [rowKey]: jobData }));

      if (jobData.status === 'completed' || jobData.status === 'failed') {
        const interval = rescrapePollingRefs.current[rowKey];
        if (interval) {
          clearInterval(interval);
          delete rescrapePollingRefs.current[rowKey];
        }
      }
    } catch (err) {
      console.error('Failed to poll rescrape status:', err);
    }
  }, []);

  const startRescrape = useCallback(async (datapointId: string, date: string) => {
    const rowKey = rescrapeRowKey(datapointId, date);
    try {
      setRescrapeJobs((prev) => ({
        ...prev,
        [rowKey]: { jobId: '', status: 'pending' },
      }));

      const response = await apiClient.post(rescrapeUrl, {
        startDate: date,
        endDate: date,
        dataPointId: datapointId,
      });

      const jobData: IRescrapeJob = response.data;
      setRescrapeJobs((prev) => ({ ...prev, [rowKey]: jobData }));

      if (jobData.jobId) {
        rescrapePollingRefs.current[rowKey] = setInterval(() => {
          pollRescrapeJob(jobData.jobId, rowKey);
        }, 2000);
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        const errorData = err.response?.data;
        const existingJobId = errorData?.jobId;
        if (existingJobId) {
          setRescrapeJobs((prev) => ({
            ...prev,
            [rowKey]: {
              jobId: existingJobId,
              status: errorData.status || 'running',
              progress: errorData.progress,
            },
          }));
          rescrapePollingRefs.current[rowKey] = setInterval(() => {
            pollRescrapeJob(existingJobId, rowKey);
          }, 2000);
        }
      } else {
        setRescrapeJobs((prev) => ({
          ...prev,
          [rowKey]: { jobId: '', status: 'failed' },
        }));
      }
    }
  }, [pollRescrapeJob]);

  useEffect(() => {
    return () => {
      Object.values(rescrapePollingRefs.current).forEach(clearInterval);
      rescrapePollingRefs.current = {};
    };
  }, []);

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !emailAddresses.includes(email)) {
      setEmailAddresses((prev) => [...prev, email]);
    }
    setEmailInput('');
  };

  const removeEmail = (email: string) => {
    setEmailAddresses((prev) => prev.filter((e) => e !== email));
  };

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const pollStatus = useCallback(async (id: string) => {
    try {
      const response = await apiClient.get(`${validationBaseUrl}/status/${id}`);
      const data: IValidationStatusResponse = response.data;

      if (data.phases) {
        setPhases(data.phases);
      }

      if (data.status === 'completed') {
        setState('done');
        setDayResults(data.dayResults || []);
        if (data.warning) setWarning(data.warning);
        stopPolling();
      } else if (data.status === 'failed') {
        setState('done');
        setError(data.error || 'Validation failed');
        stopPolling();
      }
    } catch (err: any) {
      console.error('Polling error:', err);
    }
  }, [stopPolling]);

  const startValidation = useCallback(async () => {
    if (selectedAdServers.length === 0) return;

    setState('running');
    setError(null);
    setWarning(null);
    setDayResults([]);
    setPhases([]);

    try {
      const body: any = {
        startDate,
        endDate,
        forceValidation: fullScan,
        adServers: selectedAdServers,
      };
      if (emailResults && emailAddresses.length > 0) {
        body.emails = emailAddresses;
      }

      const response = await apiClient.post(`${validationBaseUrl}/start`, body);

      const data = response.data;
      setJobId(data.jobId);
      setPhases(data.phases || []);

      pollingRef.current = setInterval(() => pollStatus(data.jobId), 4000);
    } catch (err: any) {
      setState('done');
      setError(err?.response?.data?.message || err?.message || 'Failed to start validation');
    }
  }, [startDate, endDate, fullScan, selectedAdServers, emailResults, emailAddresses, pollStatus]);

  const reset = useCallback(() => {
    stopPolling();
    setState('idle');
    setPhases([]);
    setDayResults([]);
    setJobId(null);
    setError(null);
    setWarning(null);
  }, [stopPolling]);

  const toggleAdServer = (server: string) => {
    setSelectedAdServers((prev) =>
      prev.includes(server) ? prev.filter((s) => s !== server) : [...prev, server]
    );
  };

  const toggleAllAdServers = () => {
    setSelectedAdServers((prev) =>
      prev.length === ALL_AD_SERVERS.length ? [] : [...ALL_AD_SERVERS]
    );
  };

  const activePhase = phases.find((p) => p.status === 'running') || null;
  const lastCompletedPhase = [...phases].reverse().find((p) => p.status === 'completed') || null;
  const firstPendingPhase = phases.find((p) => p.status === 'pending') || null;
  const displayPhase = activePhase || lastCompletedPhase || (state === 'running' ? (firstPendingPhase || phases[0] || null) : (state === 'done' ? phases[phases.length - 1] : null));

  const allDays = phases.flatMap((p) => p.days);
  const totalFailCount = allDays.filter((d) => d.status === 'fail').length;
  const completedPhasesCount = phases.filter((p) => p.status === 'completed').length;
  const failedResults = dayResults.filter((r) => r.status !== 'pass');

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <VerifiedUser sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Data Validation
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            — Verify data integrity before reporting
          </Typography>
          {state === 'running' && activePhase && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>
              Validating {activePhase.adServer} ({completedPhasesCount + 1}/{phases.length})...
            </Typography>
          )}
          {state === 'done' && (
            <Chip
              label={totalFailCount > 0 ? `${failedResults.length} issue${failedResults.length !== 1 ? 's' : ''} found` : 'All clear'}
              size="small"
              color={totalFailCount > 0 ? 'error' : 'success'}
              variant="filled"
              sx={{ ml: 'auto', fontWeight: 600, fontSize: 11 }}
            />
          )}
        </Box>

        {/* Info bar */}
        <Box
          sx={{
            px: 3,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#f0f7ff',
            borderBottom: '1px solid',
            borderColor: '#d0e3f7',
          }}
        >
          <InfoOutlined sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
            Run validation before generating reports to ensure scraped metrics match stored data. This helps catch discrepancies early.
          </Typography>
        </Box>

        {/* Controls */}
        <Box sx={{ px: 3, py: 2.5 }}>
          {/* Date range row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                Start
              </Typography>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={state === 'running'}
                sx={{ width: 155, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
              />
            </Box>
            <Typography color="text.disabled" sx={{ fontSize: 16, fontWeight: 300 }}>→</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                End
              </Typography>
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={state === 'running'}
                sx={{ width: 155, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
              />
            </Box>

            <Box sx={{ width: '1px', height: 28, bgcolor: 'grey.300', mx: 1 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: fullScan ? 'warning.lighter' : 'transparent',
                border: '1px solid',
                borderColor: fullScan ? 'warning.light' : 'transparent',
                borderRadius: 2,
                px: fullScan ? 1 : 0,
                py: 0.25,
                transition: 'all 0.2s',
              }}
            >
              <Switch
                checked={fullScan}
                onChange={() => {
                  if (!fullScan) {
                    setFullScanWarningOpen(true);
                  } else {
                    setFullScan(false);
                  }
                }}
                disabled={state === 'running'}
                size="small"
                color="warning"
              />
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, color: fullScan ? 'warning.dark' : 'text.secondary' }}>
                Full Scan
              </Typography>
            </Box>

            <Box sx={{ width: '1px', height: 28, bgcolor: 'grey.300', mx: 1 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: emailResults ? 'info.lighter' : 'transparent',
                border: '1px solid',
                borderColor: emailResults ? 'info.light' : 'transparent',
                borderRadius: 2,
                px: emailResults ? 1 : 0,
                py: 0.25,
                transition: 'all 0.2s',
              }}
            >
              <Switch
                checked={emailResults}
                onChange={() => setEmailResults((prev) => !prev)}
                disabled={state === 'running'}
                size="small"
                color="info"
              />
              <Email sx={{ fontSize: 16, color: emailResults ? 'info.main' : 'text.disabled' }} />
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, color: emailResults ? 'info.dark' : 'text.secondary', whiteSpace: 'nowrap' }}>
                Email Results
              </Typography>
              {emailResults && (
                <Box
                  sx={{
                    ml: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.5,
                    minWidth: 220,
                    maxWidth: 400,
                    cursor: 'text',
                  }}
                  onClick={() => {
                    const input = document.getElementById('email-chip-input');
                    if (input) input.focus();
                  }}
                >
                  {emailAddresses.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      size="small"
                      color="info"
                      onDelete={state === 'running' ? undefined : () => removeEmail(email)}
                      sx={{ fontSize: 12, fontWeight: 500, height: 24 }}
                    />
                  ))}
                  <input
                    id="email-chip-input"
                    type="email"
                    placeholder={emailAddresses.length === 0 ? 'you@email.com' : ''}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    disabled={state === 'running'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                        e.preventDefault();
                        addEmail(emailInput);
                      }
                      if (e.key === 'Backspace' && emailInput === '' && emailAddresses.length > 0) {
                        removeEmail(emailAddresses[emailAddresses.length - 1]);
                      }
                    }}
                    onBlur={() => {
                      if (emailInput.trim()) addEmail(emailInput);
                    }}
                    style={{
                      border: 'none',
                      outline: 'none',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      minWidth: 100,
                      flex: 1,
                      padding: '2px 0',
                      background: 'transparent',
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              {state === 'done' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={reset}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={state === 'idle' || state === 'done' ? startValidation : undefined}
                disabled={state === 'running' || selectedAdServers.length === 0}
                startIcon={state === 'running' ? <Refresh sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} /> : <VerifiedUser sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: state === 'running' ? 'none' : '0 2px 8px rgba(59,130,246,0.25)',
                }}
              >
                {state === 'running' ? 'Validating...' : 'Validate'}
              </Button>
            </Box>
          </Box>

          {/* Ad Servers row */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5, fontSize: 10 }}>
              Ad Servers
            </Typography>
            <Chip
              label="All"
              size="small"
              variant={selectedAdServers.length === ALL_AD_SERVERS.length ? 'filled' : 'outlined'}
              color={selectedAdServers.length === ALL_AD_SERVERS.length ? 'primary' : 'default'}
              onClick={toggleAllAdServers}
              disabled={state === 'running'}
              sx={{ fontWeight: 500, fontSize: 12 }}
            />
            {ALL_AD_SERVERS.map((server) => (
              <Chip
                key={server}
                label={server}
                size="small"
                variant={selectedAdServers.includes(server) ? 'filled' : 'outlined'}
                color={selectedAdServers.includes(server) ? 'primary' : 'default'}
                onClick={() => toggleAdServer(server)}
                disabled={state === 'running'}
                sx={{ fontWeight: 500, fontSize: 12 }}
              />
            ))}
          </Box>
        </Box>

        {/* Progress section */}
        {phases.length > 0 && (
          <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
            {/* Phase stepper */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5, fontSize: 10 }}>
                Ad Servers
              </Typography>
              {phases.map((p, idx) => {
                const isActive = p.status === 'running';
                const isCompleted = p.status === 'completed';
                const dpCompleted = p.datapointsCompleted ?? 0;
                const dpTotal = p.datapointsTotal ?? 0;

                const chipColor = isCompleted
                  ? 'success'
                  : isActive ? 'primary' : 'default';

                const chipIcon = isCompleted
                  ? <Check sx={{ fontSize: 14 }} />
                  : isActive
                    ? <Refresh sx={{ fontSize: 14, animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                    : undefined;

                const label = isCompleted
                  ? `${p.adServer} (${dpTotal} dp)`
                  : isActive && dpTotal > 0
                    ? `${p.adServer} (${dpCompleted + 1}/${dpTotal} dp)`
                    : p.adServer;

                return (
                  <Chip
                    key={p.adServer}
                    label={label}
                    size="small"
                    color={chipColor as any}
                    variant={isActive || isCompleted ? 'filled' : 'outlined'}
                    icon={chipIcon}
                    sx={{ fontWeight: 600, fontSize: 12 }}
                  />
                );
              })}
            </Box>

            {/* Active phase progress bar + day chips */}
            {displayPhase && (() => {
              const pDays = displayPhase.days;
              const passCount = pDays.filter((d) => d.status === 'pass' || d.status === 'skipped').length;
              const failCount = pDays.filter((d) => d.status === 'fail').length;
              const doneCount = passCount + failCount;
              const dpCompleted = displayPhase.datapointsCompleted ?? 0;
              const dpTotal = displayPhase.datapointsTotal ?? 0;

              return (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11 }}>
                      Progress — {displayPhase.adServer}{displayPhase.currentDatapoint ? ` — ${displayPhase.currentDatapoint}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11 }}>
                      {doneCount}/{pDays.length} days{dpTotal > 0 ? ` · dp ${dpCompleted + 1}/${dpTotal}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                    <Box sx={{ width: `${(passCount / pDays.length) * 100}%`, bgcolor: 'success.main', transition: 'width 0.3s' }} />
                    <Box sx={{ width: `${(failCount / pDays.length) * 100}%`, bgcolor: 'error.main', transition: 'width 0.3s' }} />
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
                    {pDays.map((d) => {
                      const isPass = d.status === 'pass' || d.status === 'skipped';
                      const chipColor =
                        isPass ? 'success' :
                          d.status === 'fail' ? 'error' :
                            'default';

                      const chipVariant = d.status === 'pending' ? 'outlined' : 'filled';

                      const icon =
                        isPass ? <Check sx={{ fontSize: 14 }} /> :
                          d.status === 'fail' ? <Close sx={{ fontSize: 14 }} /> :
                            undefined;

                      return (
                        <Chip
                          key={d.date}
                          label={fmtShort(d.date)}
                          size="small"
                          color={chipColor as any}
                          variant={chipVariant}
                          icon={icon}
                          sx={{
                            minWidth: 95,
                            justifyContent: 'center',
                            fontWeight: 500,
                            fontSize: 12,
                            ...(d.status === 'pending' && state === 'running' && {
                              animation: 'pulse 1.5s ease infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                              },
                            }),
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}

        {/* Email notification banner */}
        {state === 'running' && emailResults && emailAddresses.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#eef6ff',
              borderTop: '1px solid',
              borderColor: '#c7ddf7',
            }}
          >
            <Email sx={{ fontSize: 15, color: 'info.main', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'info.dark', lineHeight: 1.4 }}>
              Results will be emailed to <strong>{emailAddresses.join(', ')}</strong> when complete — you can safely leave this page.
            </Typography>
          </Box>
        )}

        {/* Loading indicator */}
        {state === 'running' && <LinearProgress />}
      </Paper>

      {/* Error */}
      {error && (
        <Paper elevation={0} sx={{ mt: 2, p: 3, border: '1px solid', borderColor: 'error.light', borderRadius: 2, bgcolor: 'error.lighter' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            <Typography color="error" sx={{ fontWeight: 600 }}>Validation Error</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{error}</Typography>
        </Paper>
      )}

      {/* Warning */}
      {warning && !error && (
        <Paper elevation={0} sx={{ mt: 2, p: 3, border: '1px solid', borderColor: 'warning.light', borderRadius: 2, bgcolor: '#fffbeb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: 'warning.main' }} />
            <Typography sx={{ fontWeight: 600, color: 'warning.dark' }}>Warning</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{warning}</Typography>
        </Paper>
      )}

      {/* Failures Report */}
      {state === 'done' && !error && failedResults.length > 0 && (
        <Paper
          elevation={1}
          sx={{ mt: 2.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}
        >
          <Box
            sx={{
              px: 3, py: 2,
              borderBottom: '1px solid', borderColor: 'grey.200',
              display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: '#fef2f2',
            }}
          >
            <Warning sx={{ color: 'error.main', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.dark' }}>
              Validation Failures Report
            </Typography>
            <Chip
              label={`${failedResults.length} failed`}
              size="small"
              color="error"
              sx={{ ml: 'auto', fontWeight: 600 }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {['Status', 'Datapoint', 'Date', 'Stored', 'Scraped', 'Difference', 'Notes', ''].map((h) => (
                    <TableCell key={h || 'rescrape'} sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {failedResults.map((r, idx) => (
                  <TableRow key={`${r.date}-${r.datapoint}-${idx}`} sx={{ verticalAlign: 'top', '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={r.status === 'error' ? 'ERROR' : 'FAIL'}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13, py: 1.5 }}>{r.datapoint}</TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap', py: 1.5 }}>{r.date}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {r.stored ? (
                        <MetricBlock impressions={r.stored.impressions} cost={r.stored.cost} revenue={r.stored.revenue} profit={r.stored.profit} />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {r.scraped ? (
                        <MetricBlock impressions={r.scraped.impressions} cost={r.scraped.cost} revenue={r.scraped.revenue} profit={r.scraped.profit} />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {r.diff ? (
                        <MetricBlock impressions={r.diff.impressions} cost={r.diff.cost} revenue={r.diff.revenue} profit={r.diff.profit} isDiff />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary', py: 1.5 }}>
                      {r.error || '-'}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      {(() => {
                        const job = getRescrapeStatus(r.datapointId, r.date);
                        const isActive = job && (job.status === 'pending' || job.status === 'running');
                        const isCompleted = job?.status === 'completed';
                        const isFailed = job?.status === 'failed';
                        if (isActive) {
                          return (
                            <Tooltip title={job.progress?.currentDate ? `Processing: ${job.progress.currentDate}` : 'Rescraping...'}>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <CircularProgress size={20} thickness={5} />
                              </Box>
                            </Tooltip>
                          );
                        }
                        if (isCompleted) {
                          return (
                            <Tooltip title="Rescrape completed">
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <Check sx={{ fontSize: 20, color: 'success.main' }} />
                              </Box>
                            </Tooltip>
                          );
                        }
                        if (isFailed) {
                          return (
                            <Tooltip title="Rescrape failed">
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <Close sx={{ fontSize: 20, color: 'error.main' }} />
                              </Box>
                            </Tooltip>
                          );
                        }
                        return r.datapointId ? (
                          <Tooltip title={isAnyRescraping ? 'A rescrape is already in progress' : `Rescrape ${r.datapoint} for ${r.date}`}>
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <IconButton
                                size="small"
                                disabled={isAnyRescraping}
                                onClick={() => startRescrape(r.datapointId!, r.date)}
                                sx={{ color: isAnyRescraping ? 'text.disabled' : 'primary.main' }}
                              >
                                <Refresh sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        ) : '-';
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* All Clear */}
      {state === 'done' && !error && failedResults.length === 0 && allDays.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 2.5, p: 4, textAlign: 'center',
            border: '1px solid', borderColor: 'success.light',
            borderRadius: 3, bgcolor: '#f0fdf4',
          }}
        >
          <Check sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
            All Days Validated Successfully
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {allDays.length} days checked across {phases.length} ad server{phases.length !== 1 ? 's' : ''} — no discrepancies found
          </Typography>
        </Paper>
      )}

      {/* Full Scan Warning Dialog */}
      <Dialog
        open={fullScanWarningOpen}
        onClose={() => setFullScanWarningOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Warning sx={{ color: 'warning.main', fontSize: 26 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Enable Full Scan?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Full Scan re-validates <strong>all days</strong> in the selected range, including those already marked as validated.
          </Typography>
          <Box sx={{ bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light', borderRadius: 2, p: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              When should you use this?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Only enable Full Scan if you suspect a code issue, data corruption, or need to re-check days that were previously validated. In most cases, the standard scan is sufficient and much faster.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setFullScanWarningOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setFullScan(true);
              setFullScanWarningOpen(false);
            }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Enable Full Scan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
